<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  Download,
  Upload,
  Trash2,
  AlertTriangle,
  Loader2,
  RotateCcw,
  Image,
  FileAudio,
  Layers,
  Box,
  Music2,
  Users,
  Clock,
  Package,
  Undo2
} from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { getDataProvider, exportToZip, importFromZip, downloadBlob, getExportFilename, type MigrationProgress } from '@/lib/dataProvider'

const authStore = useAuthStore()
const userEmail = computed(() => authStore.user?.email || 'export')

// Export/Import state
const isExporting = ref(false)
const isImporting = ref(false)
const exportProgress = ref('')
const importProgress = ref('')
const migrationMessage = ref('')
const migrationError = ref(false)
const importInput = ref<HTMLInputElement | null>(null)

// Import confirmation dialog
const showImportDialog = ref(false)
const importConfirmation = ref('')
const pendingImportFile = ref<File | null>(null)
const canImport = computed(() => importConfirmation.value.toUpperCase() === 'OVERWRITE')

// Undo Import state
const canUndoImport = ref(false)
const importBackupBatchId = ref<string | null>(null)
const importedDataBatchId = ref<string | null>(null)
const isUndoingImport = ref(false)

// Trash dialog
const showTrashDialog = ref(false)
const trashBatches = ref<Array<{
  groupKey: string
  batchId: string | null
  batchLabel: string
  deletedAt: string
  items: Array<{ id: string; table: string; name: string }>
}>>([])
const trashItemCount = ref(0)
const isLoadingTrash = ref(false)
const restoringBatches = ref(new Set<string>())
const isRestoringAll = ref(false)
const isEmptyingTrash = ref(false)

// Tables to check for deleted items
const TRASH_TABLES = [
  'scenes',
  'displays',
  'graph_containers', 
  'display_layers',
  'scene_display_configs',
  'image_library',
  'music_nodes',
  'playlist_nodes',
  'spotify_nodes'
]

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

async function exportData() {
  isExporting.value = true
  exportProgress.value = 'Starting...'
  migrationMessage.value = ''
  migrationError.value = false
  
  try {
    const provider = getDataProvider()
    const zipBlob = await exportToZip(provider, (progress: MigrationProgress) => {
      switch (progress.phase) {
        case 'tables':
          exportProgress.value = `Tables ${progress.current}/${progress.total}`
          break
        case 'files':
          exportProgress.value = progress.total > 0 
            ? `Files ${progress.current}/${progress.total}`
            : 'Scanning files...'
          break
        case 'packaging':
          exportProgress.value = 'Creating ZIP...'
          break
      }
    })
    
    downloadBlob(zipBlob, getExportFilename(userEmail.value))
    migrationMessage.value = `Export complete! (${formatBytes(zipBlob.size)})`
    migrationError.value = false
    return zipBlob
  } catch (error) {
    console.error('Export failed:', error)
    migrationMessage.value = `Export failed: ${error}`
    migrationError.value = true
    return null
  } finally {
    isExporting.value = false
    exportProgress.value = ''
  }
}

function openImportDialog() {
  importInput.value?.click()
}

function handleImportFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  
  input.value = ''
  pendingImportFile.value = file
  importConfirmation.value = ''
  showImportDialog.value = true
}

async function exportBeforeImport() {
  await exportData()
}

function cancelImport() {
  showImportDialog.value = false
  pendingImportFile.value = null
  importConfirmation.value = ''
}

async function confirmImport() {
  if (!canImport.value || !pendingImportFile.value) return
  
  showImportDialog.value = false
  const file = pendingImportFile.value
  pendingImportFile.value = null
  importConfirmation.value = ''
  
  await performImport(file)
}

async function performImport(file: File) {
  isImporting.value = true
  importProgress.value = 'Reading...'
  migrationMessage.value = ''
  migrationError.value = false
  
  try {
    const provider = getDataProvider()
    const result = await importFromZip(provider, file, {
      clearExisting: true,
      preserveHueLights: true,
      onProgress: (progress: MigrationProgress) => {
        switch (progress.phase) {
          case 'extracting':
            importProgress.value = 'Extracting...'
            break
          case 'importing':
            importProgress.value = progress.total > 0
              ? `Tables ${progress.current}/${progress.total}`
              : 'Importing...'
            break
          case 'files':
            importProgress.value = progress.total > 0
              ? `Files ${progress.current}/${progress.total}`
              : 'Importing files...'
            break
        }
      }
    })
    
    if (result.success) {
      migrationMessage.value = `Imported ${result.recordsImported} records, ${result.filesImported} files`
      if (result.warnings.length > 0) {
        migrationMessage.value += ` (${result.warnings.length} warnings)`
      }
      migrationError.value = false
      migrationMessage.value += ' — Reload to see changes'
      await loadTrashCount()
      await checkUndoImportAvailable()
    } else {
      migrationMessage.value = `Import failed: ${result.error}`
      migrationError.value = true
    }
  } catch (error) {
    console.error('Import failed:', error)
    migrationMessage.value = `Import failed: ${error}`
    migrationError.value = true
  } finally {
    isImporting.value = false
    importProgress.value = ''
  }
}

// Check if there's an import that can be undone
async function checkUndoImportAvailable() {
  try {
    const provider = getDataProvider()
    
    // Check for "Import Backup" in trash (soft-deleted old data)
    let backupBatchId: string | null = null
    for (const table of TRASH_TABLES) {
      const items = await provider.query<{ deletion_batch_id?: string | null; deleted_at?: string | null }>(table, { 
        includeDeleted: true,
        filters: [
          { column: 'deleted_at', operator: 'is', value: 'not_null' },
          { column: 'deletion_batch_id', operator: 'like', value: 'Import Backup%' }
        ]
      })
      if (items.length > 0 && items[0].deletion_batch_id) {
        backupBatchId = items[0].deletion_batch_id
        break
      }
    }
    
    // Check for "Imported Data" in active records
    let importedBatchId: string | null = null
    for (const table of TRASH_TABLES) {
      const items = await provider.query<{ deletion_batch_id?: string | null }>(table, { 
        filters: [
          { column: 'deletion_batch_id', operator: 'like', value: 'Imported Data%' }
        ]
      })
      if (items.length > 0 && items[0].deletion_batch_id) {
        importedBatchId = items[0].deletion_batch_id
        break
      }
    }
    
    canUndoImport.value = !!backupBatchId && !!importedBatchId
    importBackupBatchId.value = backupBatchId
    importedDataBatchId.value = importedBatchId
  } catch (error) {
    console.error('Failed to check undo import availability:', error)
    canUndoImport.value = false
  }
}

// Undo the last import: delete imported data, restore backup
async function undoImport() {
  if (!confirm('This will delete all imported data and restore your previous data. Continue?')) {
    return
  }
  
  isUndoingImport.value = true
  migrationMessage.value = ''
  
  try {
    const provider = getDataProvider()
    
    // Step 1: Hard delete all imported data (records with importedDataBatchId)
    if (importedDataBatchId.value) {
      for (const table of TRASH_TABLES) {
        const items = await provider.query<{ id: string; deletion_batch_id?: string | null }>(table, { 
          filters: [
            { column: 'deletion_batch_id', operator: 'eq', value: importedDataBatchId.value }
          ]
        })
        for (const item of items) {
          await provider.hardDelete(table, item.id)
        }
      }
    }
    
    // Step 2: Restore all backup data
    if (importBackupBatchId.value) {
      await provider.restoreBatch(importBackupBatchId.value)
    }
    
    migrationMessage.value = 'Import undone successfully. Reload to see your restored data.'
    migrationError.value = false
    canUndoImport.value = false
    importBackupBatchId.value = null
    importedDataBatchId.value = null
    await loadTrashCount()
  } catch (error) {
    console.error('Failed to undo import:', error)
    migrationMessage.value = `Failed to undo import: ${error}`
    migrationError.value = true
  } finally {
    isUndoingImport.value = false
  }
}

// Trash functions
async function loadTrashCount() {
  try {
    const provider = getDataProvider()
    let count = 0
    
    for (const table of TRASH_TABLES) {
      const items = await provider.query<{ deletion_batch_id?: string | null; deleted_at?: string | null }>(table, { includeDeleted: true })
      count += items.filter(item => item.deleted_at != null).length
    }
    
    trashItemCount.value = count
  } catch (error) {
    console.error('Failed to load trash count:', error)
  }
}

async function loadTrashBatches() {
  isLoadingTrash.value = true
  trashBatches.value = []
  
  try {
    const provider = getDataProvider()
    const batchMap = new Map<string, {
      groupKey: string
      batchId: string | null
      batchLabel: string
      deletedAt: string
      items: Array<{ id: string; table: string; name: string }>
    }>()
    
    for (const table of TRASH_TABLES) {
      const items = await provider.query<{
        id: string
        deletion_batch_id?: string | null
        deleted_at?: string | null
        name?: string
        title?: string
        filename?: string
        [key: string]: unknown
      }>(table, { includeDeleted: true })
      
      const deletedItems = items.filter(item => item.deleted_at != null)
      
      for (const item of deletedItems) {
        const deletedAt = item.deleted_at!
        const dateKey = new Date(deletedAt).toISOString().split('T')[0] // Group by date (YYYY-MM-DD)
        
        // Batch label is now the deletion_batch_id itself (human-readable) or generated
        let batchLabel: string
        let batchId: string | null = item.deletion_batch_id || null
        
        if (batchId) {
          // Use the batch ID directly as label (it's human-readable now)
          batchLabel = batchId
        } else {
          // Generate a descriptive label for single deletions
          const itemName = getItemDisplayName(item, table)
          batchLabel = `Deleted "${itemName}"`
        }
        
        // Group key combines batch label + date for grouping
        const groupKey = `${batchLabel}_${dateKey}`
        
        if (!batchMap.has(groupKey)) {
          batchMap.set(groupKey, {
            groupKey,
            batchId,
            batchLabel,
            deletedAt,
            items: []
          })
        }
        
        const batch = batchMap.get(groupKey)!
        batch.items.push({
          id: item.id,
          table,
          name: getItemDisplayName(item, table)
        })
      }
    }
    
    // Sort batches by deletion date, newest first
    trashBatches.value = Array.from(batchMap.values())
      .sort((a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime())
    
    trashItemCount.value = trashBatches.value.reduce((sum, b) => sum + b.items.length, 0)
  } catch (error) {
    console.error('Failed to load trash batches:', error)
  } finally {
    isLoadingTrash.value = false
  }
}

function openTrashDialog() {
  showTrashDialog.value = true
  loadTrashBatches()
}

function getItemDisplayName(item: Record<string, unknown>, table: string): string {
  if (item.name && typeof item.name === 'string') return item.name
  if (item.title && typeof item.title === 'string') return item.title
  if (item.filename && typeof item.filename === 'string') return item.filename
  if (table === 'scenes') return `Scene ${String(item.id).slice(0, 8)}...`
  return String(item.id).slice(0, 8) + '...'
}

function getTableIcon(table: string) {
  const icons: Record<string, unknown> = {
    scenes: Layers,
    graph_containers: Box,
    display_layers: Layers,
    image_library: Image,
    audio_library: FileAudio,
    audio_library_files: FileAudio,
    music_nodes: Music2,
    playlist_nodes: Music2
  }
  return icons[table] || Box
}

function formatTableName(table: string): string {
  return table.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

async function restoreBatch(groupKey: string) {
  restoringBatches.value.add(groupKey)
  
  try {
    const provider = getDataProvider()
    const batch = trashBatches.value.find(b => b.groupKey === groupKey)
    if (!batch) return
    
    // Special handling for "Import Backup" batches:
    // We need to delete the corresponding "Imported Data" first to avoid conflicts
    if (batch.batchId?.startsWith('Import Backup')) {
      // Extract timestamp from batch ID to find matching imported data
      const timestamp = batch.batchId.replace('Import Backup ', '')
      const importedDataBatchId = `Imported Data ${timestamp}`
      
      // Hard delete all imported data with matching timestamp
      for (const table of TRASH_TABLES) {
        const items = await provider.query<{ id: string; deletion_batch_id?: string | null }>(table, { 
          filters: [
            { column: 'deletion_batch_id', operator: 'eq', value: importedDataBatchId }
          ]
        })
        for (const item of items) {
          await provider.hardDelete(table, item.id)
        }
      }
      
      // Also remove any "Imported Data" batch from the UI list
      trashBatches.value = trashBatches.value.filter(b => b.batchId !== importedDataBatchId)
    }
    
    // For batches with a real batchId, use restoreBatch for efficiency
    // For single item deletions (no batchId), restore individually
    if (batch.batchId) {
      await provider.restoreBatch(batch.batchId)
    } else {
      for (const item of batch.items) {
        await provider.restore(item.table, item.id)
      }
    }
    
    // Remove batch from list
    trashBatches.value = trashBatches.value.filter(b => b.groupKey !== groupKey)
    trashItemCount.value = trashBatches.value.reduce((sum, b) => sum + b.items.length, 0)
  } catch (error) {
    console.error('Failed to restore batch:', error)
  } finally {
    restoringBatches.value.delete(groupKey)
  }
}

async function restoreAllItems() {
  isRestoringAll.value = true
  
  try {
    const provider = getDataProvider()
    
    for (const table of TRASH_TABLES) {
      await provider.restoreMany(table, [{ column: 'deleted_at', operator: 'is', value: 'not_null' }])
    }
    
    trashBatches.value = []
    trashItemCount.value = 0
    showTrashDialog.value = false
    migrationMessage.value = 'All items restored successfully'
    migrationError.value = false
  } catch (error) {
    console.error('Failed to restore all items:', error)
    migrationMessage.value = `Failed to restore: ${error}`
    migrationError.value = true
  } finally {
    isRestoringAll.value = false
  }
}

async function emptyTrash() {
  if (!confirm('This will permanently delete all items in trash. This cannot be undone!')) {
    return
  }
  
  isEmptyingTrash.value = true
  
  try {
    const provider = getDataProvider()
    
    // Tables with storage files that need cleanup
    const STORAGE_FILE_TABLES: Record<string, { bucket: string; pathColumn: string; coverColumn?: string }> = {
      'audio_library_files': { bucket: 'user-audio', pathColumn: 'storage_path', coverColumn: 'cover_storage_path' },
      'music_nodes': { bucket: 'user-audio', pathColumn: 'storage_path', coverColumn: 'cover_storage_path' },
      'image_library': { bucket: 'user-images', pathColumn: 'storage_path' }
    }
    
    for (const table of TRASH_TABLES) {
      const items = await provider.query<Record<string, unknown>>(table, { includeDeleted: true })
      const deletedItems = items.filter(item => item.deleted_at != null)
      
      for (const item of deletedItems) {
        // Delete storage files for tables that have them
        const storageConfig = STORAGE_FILE_TABLES[table]
        if (storageConfig) {
          const storagePath = item[storageConfig.pathColumn] as string | null
          if (storagePath) {
            await provider.deleteFile(storageConfig.bucket, storagePath)
          }
          if (storageConfig.coverColumn) {
            const coverPath = item[storageConfig.coverColumn] as string | null
            if (coverPath) {
              await provider.deleteFile(storageConfig.bucket, coverPath)
            }
          }
        }
        
        // Hard delete the database record
        await provider.hardDelete(table, item.id as string)
      }
    }
    
    trashBatches.value = []
    trashItemCount.value = 0
    showTrashDialog.value = false
    migrationMessage.value = 'Trash emptied successfully'
    migrationError.value = false
  } catch (error) {
    console.error('Failed to empty trash:', error)
    migrationMessage.value = `Failed to empty trash: ${error}`
    migrationError.value = true
  } finally {
    isEmptyingTrash.value = false
  }
}

onMounted(() => {
  loadTrashCount()
  checkUndoImportAvailable()
})
</script>

<template>
  <div class="space-y-6">
    <!-- Section Header -->
    <div>
      <h3 class="text-sm font-medium">Data Management</h3>
      <p class="text-xs text-muted-foreground mt-1">Export, import, and manage your data</p>
    </div>

    <!-- Backup & Restore Section -->
    <div class="rounded-xl border bg-card p-4 space-y-4">
      <div class="flex items-center gap-2">
        <div class="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
          <Package class="h-4 w-4 text-blue-500" />
        </div>
        <div>
          <h4 class="text-sm font-medium">Backup & Restore</h4>
          <p class="text-xs text-muted-foreground">Export or import all your data</p>
        </div>
      </div>
      
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          class="flex-1"
          @click="exportData"
          :disabled="isExporting"
        >
          <Download v-if="!isExporting" class="h-4 w-4 mr-2" />
          <Loader2 v-else class="h-4 w-4 mr-2 animate-spin" />
          {{ exportProgress || 'Export All' }}
        </Button>
        <Button
          variant="outline"
          size="sm"
          class="flex-1"
          @click="openImportDialog"
          :disabled="isImporting"
        >
          <Upload v-if="!isImporting" class="h-4 w-4 mr-2" />
          <Loader2 v-else class="h-4 w-4 mr-2 animate-spin" />
          {{ importProgress || 'Import' }}
        </Button>
        <input
          ref="importInput"
          type="file"
          accept=".zip"
          class="hidden"
          @change="handleImportFileSelected"
        />
      </div>
      
      <div v-if="migrationMessage" class="text-xs px-2 py-1.5 rounded-lg" :class="migrationError ? 'text-destructive bg-destructive/10' : 'text-green-600 bg-green-500/10'">
        {{ migrationMessage }}
      </div>
      
      <!-- Undo Import Button -->
      <div v-if="canUndoImport" class="pt-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          class="w-full"
          @click="undoImport"
          :disabled="isUndoingImport"
        >
          <Loader2 v-if="isUndoingImport" class="h-4 w-4 mr-2 animate-spin" />
          <Undo2 v-else class="h-4 w-4 mr-2" />
          {{ isUndoingImport ? 'Restoring...' : 'Undo Last Import' }}
        </Button>
        <p class="text-xs text-muted-foreground mt-2">
          Delete imported data and restore your previous data.
        </p>
      </div>
    </div>

    <!-- Trash Section -->
    <div class="rounded-xl border bg-card p-4 space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
            <Trash2 class="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <h4 class="text-sm font-medium">Trash</h4>
            <p class="text-xs text-muted-foreground">
              <span v-if="trashItemCount > 0">{{ trashItemCount }} item{{ trashItemCount !== 1 ? 's' : '' }} in trash</span>
              <span v-else>No deleted items</span>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          @click="openTrashDialog"
          :disabled="trashItemCount === 0"
        >
          <Clock class="h-4 w-4 mr-2" />
          View
        </Button>
      </div>
    </div>

    <!-- Import Confirmation Dialog -->
    <Dialog v-model:open="showImportDialog">
      <DialogContent>
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2 text-destructive">
            <AlertTriangle class="h-5 w-5" />
            Import Will Overwrite All Data
          </DialogTitle>
          <DialogDescription as="div" class="space-y-3">
            <p>
              Importing will <strong>move your existing data to trash</strong>. If import fails, your data will be automatically restored.
            </p>
            <ul class="list-disc list-inside text-sm space-y-1 text-muted-foreground">
              <li>All scenes and display configurations</li>
              <li>Images and audio files</li>
              <li>Music nodes and playlists</li>
              <li>Hue bridge configurations</li>
            </ul>
            <div class="pt-2 space-y-2">
              <p class="text-sm font-medium">Would you like to create a backup first?</p>
              <Button 
                variant="outline" 
                size="sm" 
                class="w-full"
                @click="exportBeforeImport"
                :disabled="isExporting"
              >
                <Download class="h-4 w-4 mr-2" />
                {{ isExporting ? 'Creating backup...' : 'Create Backup First' }}
              </Button>
            </div>
            <div class="pt-2">
              <p class="text-sm mb-2">To confirm import, type <strong>OVERWRITE</strong>:</p>
              <Input 
                v-model="importConfirmation" 
                placeholder="Type OVERWRITE to confirm"
                class="font-mono"
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="cancelImport" :disabled="isImporting">
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            @click="confirmImport"
            :disabled="!canImport || isImporting"
          >
            {{ isImporting ? 'Importing...' : 'Import & Overwrite' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Trash Dialog -->
    <Dialog v-model:open="showTrashDialog">
      <DialogContent class="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <Trash2 class="h-5 w-5" />
            Trash
          </DialogTitle>
          <DialogDescription>
            Items are grouped by deletion operation. Restore entire groups to prevent orphaned data.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4 py-2 max-h-[50vh] overflow-y-auto">
          <!-- Loading State -->
          <div v-if="isLoadingTrash" class="flex items-center justify-center py-8">
            <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
            <span class="ml-2 text-muted-foreground">Loading deleted items...</span>
          </div>

          <!-- Empty State -->
          <div v-else-if="trashBatches.length === 0" class="text-center py-8 text-muted-foreground">
            <Trash2 class="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Trash is empty</p>
          </div>

          <!-- Batches -->
          <div v-else class="space-y-4">
            <div 
              v-for="batch in trashBatches" 
              :key="batch.groupKey"
              class="border border-border rounded-lg p-3 space-y-2"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-medium">{{ batch.batchLabel }}</h4>
                  <p class="text-xs text-muted-foreground">
                    {{ batch.items.length }} item{{ batch.items.length !== 1 ? 's' : '' }} • 
                    {{ formatRelativeTime(batch.deletedAt) }}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  @click="restoreBatch(batch.groupKey)"
                  :disabled="restoringBatches.has(batch.groupKey)"
                >
                  <Loader2 v-if="restoringBatches.has(batch.groupKey)" class="h-4 w-4 animate-spin" />
                  <RotateCcw v-else class="h-4 w-4 mr-1" />
                  <span v-if="!restoringBatches.has(batch.groupKey)">Restore All</span>
                </Button>
              </div>
              
              <!-- Item preview (collapsed) -->
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="item in batch.items.slice(0, 5)"
                  :key="item.id"
                  class="inline-flex items-center gap-1 text-xs px-2 py-1 bg-muted rounded"
                >
                  <component :is="getTableIcon(item.table)" class="h-3 w-3" />
                  {{ item.name }}
                </span>
                <span 
                  v-if="batch.items.length > 5" 
                  class="text-xs px-2 py-1 text-muted-foreground"
                >
                  +{{ batch.items.length - 5 }} more
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter class="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            @click="restoreAllItems"
            :disabled="trashBatches.length === 0 || isRestoringAll"
            class="w-full sm:w-auto"
          >
            <Loader2 v-if="isRestoringAll" class="h-4 w-4 mr-2 animate-spin" />
            <RotateCcw v-else class="h-4 w-4 mr-2" />
            Restore All
          </Button>
          <Button 
            variant="destructive" 
            @click="emptyTrash"
            :disabled="trashBatches.length === 0 || isEmptyingTrash"
            class="w-full sm:w-auto"
          >
            <Loader2 v-if="isEmptyingTrash" class="h-4 w-4 mr-2 animate-spin" />
            <Trash2 v-else class="h-4 w-4 mr-2" />
            Empty Trash
          </Button>
          <Button variant="outline" @click="showTrashDialog = false" class="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
